import { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Eye, EyeOff, Star, StarOff, Trash2, Plus, Pencil, Package, BarChart3, Users, Video, DollarSign } from "lucide-react";
import { adminApi } from "@/lib/api";
import ProductFormDialog from "@/components/ProductFormDialog";
import ImageDropzone from "@/components/ImageDropzone";
import VideoDropzone from "@/components/VideoDropzone";

const PRODUCT_SECTIONS = [
  { key: "mens",     label: "Men's Apparel",         blurb: "Men's tees & fits — cut and colours for him" },
  { key: "womens",   label: "Women's Apparel",       blurb: "Women's tees & fits — cut and colours for her" },
  { key: "apparel",  label: "Hoodies & Outerwear",   blurb: "Hoodies, blankets and unisex heavy pieces" },
  { key: "prints",   label: "Prints & Canvas",       blurb: "Fine art prints and canvas artworks" },
  { key: "diy_kits", label: "Paint-Your-Own Kits",   blurb: "Guided DIY kits for events & at-home" },
  { key: "digital",  label: "Digital Designs",       blurb: "Downloadable designs & instant delivery" },
  { key: "murals",   label: "Murals",                blurb: "Bookable mural jobs & paint-your-own mural products" },
];

export default function AdminDashboard() {
  const [tab, setTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [requests, setRequests] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [galleryForm, setGalleryForm] = useState({ title: "", description: "", image_url: "", category: "painting", sort_order: 0 });
  const [galleryEditingId, setGalleryEditingId] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [videos, setVideos] = useState([]);
  const [videoForm, setVideoForm] = useState({ title: "", description: "", video_url: "", sort_order: 0 });
  const [videoEditingId, setVideoEditingId] = useState(null);
  const [hoodieLogos, setHoodieLogos] = useState([]);
  const [logoForm, setLogoForm] = useState({ label: "", tag: "", image_url: "", recolourable: false, sort_order: 0 });
  const [logoEditingId, setLogoEditingId] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [reviewFilter, setReviewFilter] = useState("pending");
  const navigate = useNavigate();

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [p, r, c, o, g, v, hl, rv] = await Promise.all([
        adminApi.listProducts(),
        adminApi.listRequests(),
        adminApi.listContacts(),
        adminApi.listOrders(),
        adminApi.listGallery(),
        adminApi.listVideos(),
        adminApi.listHoodieLogos(),
        adminApi.listReviews(),
      ]);
      const sortedP = [...p].sort((a, b) => (a.sort_order ?? 999) - (b.sort_order ?? 999));
      const sortedG = [...g].sort((a, b) => (a.sort_order ?? 999) - (b.sort_order ?? 999));
      const sortedV = [...v].sort((a, b) => (a.sort_order ?? 999) - (b.sort_order ?? 999));
      const sortedHL = [...hl].sort((a, b) => (a.sort_order ?? 999) - (b.sort_order ?? 999));
      setProducts(sortedP); setRequests(r); setContacts(c); setOrders(o); setGallery(sortedG); setVideos(sortedV); setHoodieLogos(sortedHL); setReviews(rv);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem("cka_admin_token");
        navigate("/admin/login");
      } else {
        toast.error("Failed to load data");
      }
    } finally { setLoading(false); }
  }, [navigate]);

  useEffect(() => {
    if (!localStorage.getItem("cka_admin_token")) {
      navigate("/admin/login");
      return;
    }
    loadAll();
  }, [loadAll, navigate]);

  const logout = () => {
    localStorage.removeItem("cka_admin_token");
    navigate("/admin/login");
  };

  const toggle = async (id, key, value) => {
    try {
      const updated = await adminApi.updateProduct(id, { [key]: value });
      setProducts((prev) => prev.map((p) => (p.id === id ? updated : p)));
      toast.success("Updated");
    } catch { toast.error("Update failed"); }
  };

  const deleteProduct = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try {
      await adminApi.deleteProduct(id);
      setProducts((prev) => prev.filter((p) => p.id !== id));
      toast.success("Deleted");
    } catch { toast.error("Delete failed"); }
  };

  const updateRequestStatus = async (id, status) => {
    try {
      const updated = await adminApi.updateRequest(id, status);
      setRequests((prev) => prev.map((r) => (r.id === id ? updated : r)));
      toast.success(`Marked ${status.replace("_", " ")}`);
    } catch { toast.error("Update failed"); }
  };

  const updateOrderStatus = async (id, status) => {
    try {
      const updated = await adminApi.updateOrder(id, status);
      setOrders((prev) => prev.map((o) => (o.id === id ? updated : o)));
      toast.success(`Order marked ${status.replace("_", " ")}`);
    } catch { toast.error("Update failed"); }
  };

  const syncPrintify = null; // deprecated — Printify integration removed from UI

  const resetGalleryForm = () => {
    setGalleryForm({ title: "", description: "", image_url: "", category: "painting", sort_order: (gallery[gallery.length - 1]?.sort_order || 0) + 1 });
    setGalleryEditingId(null);
  };

  const submitGallery = async (e) => {
    e.preventDefault();
    if (!galleryForm.title.trim() || !galleryForm.image_url.trim()) {
      toast.error("Title and image URL are required");
      return;
    }
    try {
      if (galleryEditingId) {
        const updated = await adminApi.updateGallery(galleryEditingId, galleryForm);
        setGallery((prev) => prev.map((x) => (x.id === galleryEditingId ? updated : x)));
        toast.success("Gallery item updated");
      } else {
        const created = await adminApi.createGallery(galleryForm);
        setGallery((prev) => [...prev, created]);
        toast.success("Added to Studio Gallery");
      }
      resetGalleryForm();
    } catch { toast.error("Save failed"); }
  };

  const editGallery = (item) => {
    setGalleryForm({
      title: item.title,
      description: item.description || "",
      image_url: item.image_url,
      category: item.category || "painting",
      sort_order: item.sort_order ?? 0,
    });
    setGalleryEditingId(item.id);
  };

  const deleteGallery = async (id) => {
    if (!window.confirm("Delete this gallery item?")) return;
    try {
      await adminApi.deleteGallery(id);
      setGallery((prev) => prev.filter((x) => x.id !== id));
      toast.success("Deleted");
    } catch { toast.error("Delete failed"); }
  };

  const loadAnalytics = async () => {
    try {
      const data = await adminApi.getAnalytics();
      setAnalytics(data);
    } catch { toast.error("Failed to load analytics"); }
  };

  const resetVideoForm = () => {
    setVideoForm({ title: "", description: "", video_url: "", sort_order: (videos[videos.length - 1]?.sort_order || 0) + 1 });
    setVideoEditingId(null);
  };

  const submitVideo = async (e) => {
    e.preventDefault();
    if (!videoForm.title.trim() || !videoForm.video_url.trim()) {
      toast.error("Title and video are required");
      return;
    }
    try {
      if (videoEditingId) {
        const updated = await adminApi.updateVideo(videoEditingId, videoForm);
        setVideos((prev) => prev.map((x) => (x.id === videoEditingId ? updated : x)));
        toast.success("Video updated");
      } else {
        const created = await adminApi.createVideo(videoForm);
        setVideos((prev) => [...prev, created]);
        toast.success("Video added");
      }
      resetVideoForm();
    } catch { toast.error("Save failed"); }
  };

  const editVideo = (item) => {
    setVideoForm({
      title: item.title,
      description: item.description || "",
      video_url: item.video_url,
      sort_order: item.sort_order ?? 0,
    });
    setVideoEditingId(item.id);
  };

  const deleteVideo = async (id) => {
    if (!window.confirm("Delete this video?")) return;
    try {
      await adminApi.deleteVideo(id);
      setVideos((prev) => prev.filter((x) => x.id !== id));
      toast.success("Deleted");
    } catch { toast.error("Delete failed"); }
  };

  // ---------- Hoodie logos ----------
  const resetLogoForm = () => {
    setLogoForm({ label: "", tag: "", image_url: "", recolourable: false, sort_order: (hoodieLogos[hoodieLogos.length - 1]?.sort_order || 0) + 10 });
    setLogoEditingId(null);
  };

  const submitLogo = async (e) => {
    e.preventDefault();
    if (!logoForm.label.trim() || !logoForm.image_url.trim()) {
      toast.error("Label and image are required");
      return;
    }
    try {
      if (logoEditingId) {
        const updated = await adminApi.updateHoodieLogo(logoEditingId, logoForm);
        setHoodieLogos((prev) => prev.map((x) => (x.id === logoEditingId ? updated : x)));
        toast.success("Logo updated");
      } else {
        const created = await adminApi.createHoodieLogo(logoForm);
        setHoodieLogos((prev) => [...prev, created]);
        toast.success("Logo added");
      }
      resetLogoForm();
    } catch { toast.error("Save failed"); }
  };

  const editLogo = (item) => {
    setLogoForm({
      label: item.label,
      tag: item.tag || "",
      image_url: item.image_url,
      recolourable: !!item.recolourable,
      sort_order: item.sort_order ?? 0,
    });
    setLogoEditingId(item.id);
  };

  const toggleLogoVisible = async (item) => {
    try {
      const updated = await adminApi.updateHoodieLogo(item.id, { is_visible: !item.is_visible });
      setHoodieLogos((prev) => prev.map((x) => (x.id === item.id ? updated : x)));
    } catch { toast.error("Update failed"); }
  };

  const deleteLogo = async (id) => {
    if (!window.confirm("Delete this hoodie logo? It will disappear from the customiser immediately.")) return;
    try {
      await adminApi.deleteHoodieLogo(id);
      setHoodieLogos((prev) => prev.filter((x) => x.id !== id));
      toast.success("Deleted");
    } catch { toast.error("Delete failed"); }
  };

  // ---------- Reviews ----------
  const approveReview = async (r) => {
    try {
      const updated = await adminApi.updateReview(r.id, { is_approved: !r.is_approved });
      setReviews((prev) => prev.map((x) => (x.id === r.id ? updated : x)));
      toast.success(updated.is_approved ? "Review approved — now visible" : "Review hidden from site");
    } catch { toast.error("Update failed"); }
  };

  const deleteReview = async (id) => {
    if (!window.confirm("Delete this review permanently?")) return;
    try {
      await adminApi.deleteReview(id);
      setReviews((prev) => prev.filter((x) => x.id !== id));
      toast.success("Deleted");
    } catch { toast.error("Delete failed"); }
  };

  const pendingCount = reviews.filter((r) => !r.is_approved).length;
  const filteredReviews = reviews.filter((r) => reviewFilter === "all" ? true : reviewFilter === "pending" ? !r.is_approved : r.is_approved);

  return (
    <div data-testid="page-admin-dashboard" className="min-h-screen bg-[#0A0A0A] text-white">
      <header className="border-b border-white/10 sticky top-0 bg-[#0A0A0A]/90 backdrop-blur z-40">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="font-display text-2xl">CKA<span className="text-[#FF4500]">.</span></Link>
            <span className="text-white/40 text-sm uppercase tracking-widest">Admin Studio</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-white/60 hidden md:inline">{localStorage.getItem("cka_admin_email")}</span>
            <button data-testid="admin-logout" onClick={logout} className="btn-ghost text-sm py-2 px-3"><LogOut className="w-4 h-4" /> Logout</button>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-8">
        <div className="flex flex-wrap gap-2 border-b border-white/10 mb-8">
          {[
            ["products", `Products (${products.length})`],
            ["gallery", `Studio Gallery (${gallery.length})`],
            ["videos", `Videos (${videos.length})`],
            ["hoodie-logos", `Hoodie Logos (${hoodieLogos.length})`],
            ["reviews", `Reviews${pendingCount ? ` (${pendingCount} pending)` : ""}`],
            ["analytics", `Analytics`],
            ["orders", `Orders (${orders.length})`],
            ["requests", `Custom Requests (${requests.length})`],
            ["contacts", `Contacts (${contacts.length})`],
          ].map(([k, label]) => (
            <button
              key={k}
              data-testid={`admin-tab-${k}`}
              onClick={() => { setTab(k); if (k === "analytics") loadAnalytics(); }}
              className={`px-5 py-3 text-xs uppercase tracking-widest ${tab === k ? "border-b-2 border-[#FF4500] text-[#FF4500]" : "text-white/60 hover:text-white"}`}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === "products" && (
          <div>
            <div className="mb-6">
              <h2 className="font-display text-3xl">Products</h2>
              <p className="text-xs text-white/50 uppercase tracking-widest mt-1">Each category has its own section — click "Add" next to a section to create a new item there.</p>
            </div>

            {/* Legend */}
            <div className="mb-8 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
              <div className="border border-white/10 bg-[#141414] p-3 flex items-center gap-2">
                <Eye className="w-4 h-4 text-white/60 shrink-0" />
                <span className="text-white/70">Show / hide in shop</span>
              </div>
              <div className="border border-white/10 bg-[#141414] p-3 flex items-center gap-2">
                <Star className="w-4 h-4 text-[#FF4500] shrink-0" />
                <span className="text-white/70">Feature on home page</span>
              </div>
              <div className="border border-white/10 bg-[#141414] p-3 flex items-center gap-2">
                <Pencil className="w-4 h-4 text-white/60 shrink-0" />
                <span className="text-white/70">Edit title, images, price…</span>
              </div>
              <div className="border border-white/10 bg-[#141414] p-3 flex items-center gap-2">
                <Trash2 className="w-4 h-4 text-white/60 shrink-0" />
                <span className="text-white/70">Delete permanently</span>
              </div>
            </div>

            {loading ? (
              <p className="text-white/50">Loading...</p>
            ) : (
              <div className="space-y-10">
                {PRODUCT_SECTIONS.map(({ key, label, blurb }) => {
                  const items = products.filter((p) => (p.category || "prints") === key);
                  return (
                    <section key={key} data-testid={`admin-section-${key}`} className="border border-white/10 bg-[#0F0F0F]">
                      <header className="flex items-center justify-between flex-wrap gap-3 px-5 py-4 border-b border-white/10">
                        <div>
                          <h3 className="font-display text-2xl leading-none">{label} <span className="text-white/40 text-lg">({items.length})</span></h3>
                          {blurb && <p className="text-[11px] text-white/50 uppercase tracking-widest mt-1">{blurb}</p>}
                        </div>
                        <button
                          data-testid={`admin-add-${key}`}
                          onClick={() => { setEditing({ __presetCategory: key }); setDialogOpen(true); }}
                          className="btn-primary text-xs py-2 px-4"
                        >
                          <Plus className="w-4 h-4" /> Add {label}
                        </button>
                      </header>

                      {items.length === 0 ? (
                        <div className="px-5 py-10 text-center text-white/40 text-sm">
                          No {label.toLowerCase()} yet — tap "Add {label}" above to create the first one.
                        </div>
                      ) : (
                        <div className="p-3 space-y-2">
                          {items.map((p) => (
                            <div key={p.id} data-testid={`admin-product-${p.id}`} className="flex items-center gap-4 border border-white/10 bg-[#141414] p-4">
                              <img src={p.image_url} alt="" className="w-16 h-16 object-cover" />
                              <div className="flex-1 min-w-0">
                                <p className="font-display text-lg truncate">{p.title}</p>
                                <p className="text-xs text-white/50 uppercase tracking-widest">${p.price?.toFixed(2)} · {p.source}</p>
                              </div>
                              <button data-testid={`admin-toggle-visible-${p.id}`} onClick={() => toggle(p.id, "is_visible", !p.is_visible)} className="p-2 border border-white/20 hover:border-[#FF4500]" title={p.is_visible ? "Hide" : "Show"}>
                                {p.is_visible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4 text-white/40" />}
                              </button>
                              <button data-testid={`admin-toggle-featured-${p.id}`} onClick={() => toggle(p.id, "is_featured", !p.is_featured)} className="p-2 border border-white/20 hover:border-[#FF4500]" title="Featured">
                                {p.is_featured ? <Star className="w-4 h-4 text-[#FF4500] fill-[#FF4500]" /> : <StarOff className="w-4 h-4 text-white/40" />}
                              </button>
                              <button data-testid={`admin-edit-${p.id}`} onClick={() => { setEditing(p); setDialogOpen(true); }} className="p-2 border border-white/20 hover:border-[#FF4500]" title="Edit">
                                <Pencil className="w-4 h-4" />
                              </button>
                              <button data-testid={`admin-delete-${p.id}`} onClick={() => deleteProduct(p.id)} className="p-2 border border-white/20 hover:border-red-500">
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </section>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {tab === "orders" && (
          <div>
            <h2 className="font-display text-3xl mb-2">Orders</h2>
            <p className="text-xs text-white/50 uppercase tracking-widest mb-6">Update status after PayID payment is received or when shipping</p>
            {loading ? <p className="text-white/50">Loading...</p> : orders.length === 0 ? <p className="text-white/50">No orders yet.</p> : (
              <div className="space-y-4">
                {orders.map((o) => (
                  <div key={o.id} data-testid={`admin-order-${o.id}`} className="border border-white/10 bg-[#141414] p-6">
                    <div className="flex justify-between items-start flex-wrap gap-3 mb-4">
                      <div>
                        <div className="flex items-center gap-3 flex-wrap">
                          <Package className="w-5 h-5 text-[#FF4500]" />
                          <p className="font-display text-xl">{o.reference}</p>
                          <span className="text-[#FF4500] font-semibold">${o.total.toFixed(2)}</span>
                        </div>
                        <p className="text-xs text-white/50 uppercase tracking-widest mt-1">
                          {o.shipping.name} · {o.shipping.email} · {o.shipping.phone || "no phone"}
                        </p>
                        <p className="text-xs text-white/50 mt-1">
                          {o.shipping.address_line1}{o.shipping.address_line2 ? `, ${o.shipping.address_line2}` : ""}, {o.shipping.city} {o.shipping.state} {o.shipping.postcode}, {o.shipping.country}
                        </p>
                        {o.shipping.notes && <p className="text-xs text-white/60 italic mt-1">"{o.shipping.notes}"</p>}
                      </div>
                      <span className={`text-xs px-3 py-1 uppercase tracking-widest whitespace-nowrap ${
                        o.status === "awaiting_payment" ? "bg-[#FF4500] text-white" :
                        o.status === "paid" ? "bg-yellow-600 text-white" :
                        o.status === "shipped" ? "bg-blue-700 text-white" :
                        o.status === "delivered" ? "bg-green-700 text-white" : "bg-white/10 text-white/60"
                      }`}>{o.status.replace("_", " ")}</span>
                    </div>
                    <div className="border-y border-white/10 py-3 my-4 space-y-1">
                      {o.items.map((it, i) => (
                        <div key={`${o.id}-${it.product_id || "item"}-${i}`} className="flex justify-between text-sm">
                          <span>{it.title} <span className="text-white/40">× {it.qty}</span></span>
                          <span>${(it.price * it.qty).toFixed(2)}</span>
                        </div>
                      ))}
                      {o.shipping_cost > 0 && (
                        <div className="flex justify-between text-sm text-white/70 pt-2 border-t border-white/5 mt-2">
                          <span>Shipping{o.shipping_option?.name ? ` · ${o.shipping_option.name}` : ""}</span>
                          <span>${o.shipping_cost.toFixed(2)}</span>
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      {["awaiting_payment", "paid", "shipped", "delivered", "cancelled"].map((s) => (
                        <button
                          key={s}
                          data-testid={`admin-order-status-${o.id}-${s}`}
                          onClick={() => updateOrderStatus(o.id, s)}
                          className={`text-xs px-3 py-2 border ${o.status === s ? "border-[#FF4500] text-[#FF4500]" : "border-white/20 hover:border-white"} uppercase tracking-widest`}
                        >
                          {s.replace("_", " ")}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "requests" && (
          <div>
            <h2 className="font-display text-3xl mb-6">Custom Requests</h2>
            {loading ? <p className="text-white/50">Loading...</p> : requests.length === 0 ? <p className="text-white/50">No requests yet.</p> : (
              <div className="space-y-4">
                {requests.map((r) => (
                  <div key={r.id} data-testid={`admin-request-${r.id}`} className="border border-white/10 bg-[#141414] p-6">
                    <div className="flex justify-between items-start mb-3 flex-wrap gap-2">
                      <div>
                        <p className="font-display text-xl">{r.name} <span className="text-white/50 text-sm ml-2">{r.email}</span></p>
                        <p className="text-xs text-white/50 uppercase tracking-widest">{r.project_type.replace("_", " ")} · budget: {r.budget || "n/a"} · deadline: {r.deadline || "n/a"}</p>
                      </div>
                      <span className={`text-xs px-3 py-1 uppercase tracking-widest ${
                        r.status === "new" ? "bg-[#FF4500] text-white" :
                        r.status === "in_progress" ? "bg-yellow-600 text-white" :
                        r.status === "completed" ? "bg-green-700 text-white" : "bg-white/10 text-white/60"
                      }`}>{r.status}</span>
                    </div>
                    <p className="text-white/70 whitespace-pre-line mb-4">{r.description}</p>
                    {r.reference_url && <a href={r.reference_url} target="_blank" rel="noreferrer" className="text-[#FF4500] text-sm hover:underline">Reference →</a>}
                    <div className="flex gap-2 mt-4 flex-wrap">
                      {["new", "in_progress", "completed", "rejected"].map((s) => (
                        <button
                          key={s}
                          data-testid={`admin-request-status-${r.id}-${s}`}
                          onClick={() => updateRequestStatus(r.id, s)}
                          className={`text-xs px-3 py-2 border ${r.status === s ? "border-[#FF4500] text-[#FF4500]" : "border-white/20 hover:border-white"} uppercase tracking-widest`}
                        >
                          {s.replace("_", " ")}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "contacts" && (
          <div>
            <h2 className="font-display text-3xl mb-6">Contact messages</h2>
            {loading ? <p className="text-white/50">Loading...</p> : contacts.length === 0 ? <p className="text-white/50">No messages yet.</p> : (
              <div className="space-y-4">
                {contacts.map((c) => (
                  <div key={c.id} data-testid={`admin-contact-${c.id}`} className="border border-white/10 bg-[#141414] p-6">
                    <p className="font-display text-lg">{c.name} <span className="text-white/50 text-sm ml-2">{c.email}</span></p>
                    <p className="text-xs text-white/50 uppercase tracking-widest mb-2">{c.subject || "(no subject)"}</p>
                    <p className="text-white/70 whitespace-pre-line">{c.message}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "analytics" && (
          <div data-testid="admin-panel-analytics">
            <div className="flex justify-between items-end mb-8 flex-wrap gap-3">
              <div>
                <h2 className="font-display text-3xl">At a glance</h2>
                <p className="text-xs text-white/50 uppercase tracking-widest mt-1">The numbers that matter — refresh to see the latest</p>
              </div>
              <button onClick={loadAnalytics} className="text-xs uppercase tracking-widest border border-white/20 py-2 px-4 hover:border-[#FF4500] hover:text-[#FF4500]">
                Refresh
              </button>
            </div>

            {!analytics ? (
              <p className="text-white/50 text-center py-16">Loading analytics…</p>
            ) : (
              <>
                {/* 4 headline stats — the only counts Cam needs */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
                  {(() => {
                    const paidOrders = orders.filter((o) => o.status && o.status !== "cancelled" && o.status !== "awaiting_payment");
                    const revenue = paidOrders.reduce((sum, o) => sum + (o.total || 0), 0);
                    const stats = [
                      { label: "Visitors",   value: analytics.totals.unique_visitors, icon: Users,      hint: "Unique people this month" },
                      { label: "Page views", value: analytics.totals.page_views,      icon: BarChart3,  hint: "Total pages viewed" },
                      { label: "Orders",     value: orders.length,                    icon: Package,    hint: "All orders received" },
                      { label: "Revenue",    value: `$${revenue.toFixed(0)}`,         icon: DollarSign, hint: "Paid + shipped + delivered" },
                    ];
                    return stats.map((s) => (
                      <div key={s.label} className="border border-white/10 bg-[#141414] p-5">
                        <div className="flex items-center justify-between mb-3">
                          <p className="text-[10px] uppercase tracking-widest text-white/50">{s.label}</p>
                          <s.icon className="w-4 h-4 text-[#FF4500]" />
                        </div>
                        <p data-testid={`analytics-total-${s.label.toLowerCase()}`} className="font-display text-4xl">{s.value}</p>
                        <p className="text-[10px] text-white/40 mt-2 uppercase tracking-widest">{s.hint}</p>
                      </div>
                    ));
                  })()}
                </div>

                {/* Top 5 most-clicked artworks — the only detail worth keeping */}
                <div>
                  <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
                    <h3 className="font-display text-2xl">Top artworks</h3>
                    <p className="text-xs text-white/50 uppercase tracking-widest">Most clicked · top 5</p>
                  </div>
                  {analytics.product_clicks.length === 0 ? (
                    <p className="text-white/50 text-sm py-6 text-center border border-white/10 bg-[#141414]">No clicks yet — share your links to start tracking.</p>
                  ) : (
                    <div className="border border-white/10 bg-[#141414] overflow-hidden">
                      {analytics.product_clicks.slice(0, 5).map((pc, i) => (
                        <div key={pc.product_id} data-testid={`analytics-product-${pc.product_id}`} className="flex items-center gap-4 px-5 py-4 border-b border-white/5 last:border-b-0 hover:bg-white/[0.02]">
                          <span className="font-display text-xl text-[#FF4500] w-8">#{i + 1}</span>
                          <span className="flex-1 text-sm text-white/90 truncate">{pc.title}</span>
                          <span className="font-display text-2xl">{pc.total_clicks}</span>
                          <span className="text-[10px] text-white/40 uppercase tracking-widest w-16 text-right">clicks</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {tab === "videos" && (
          <div data-testid="admin-panel-videos">
            <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
              <div>
                <h2 className="font-display text-3xl">Videos · Artworks Being Made</h2>
                <p className="text-xs text-white/50 uppercase tracking-widest mt-1">Upload process clips — they appear on the Home page's "In the Making" section.</p>
              </div>
            </div>

            <form onSubmit={submitVideo} className="border border-white/10 bg-[#141414] p-5 md:p-6 grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
              <div className="md:col-span-2">
                <p className="overline mb-3">{videoEditingId ? "Edit video" : "Add a new process video"}</p>
              </div>
              <div className="md:col-span-2">
                <label className="text-[10px] uppercase tracking-widest text-white/50">Video *</label>
                <div className="mt-2">
                  <VideoDropzone
                    value={videoForm.video_url}
                    onUploaded={(url) => setVideoForm({ ...videoForm, video_url: url })}
                    onCleared={() => setVideoForm({ ...videoForm, video_url: "" })}
                    label="Drop video here or click to browse"
                  />
                </div>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-white/50">Title *</label>
                <input
                  data-testid="admin-video-title"
                  value={videoForm.title}
                  onChange={(e) => setVideoForm({ ...videoForm, title: e.target.value })}
                  className="w-full bg-[#0A0A0A] border border-white/15 px-3 py-2 mt-1 text-sm focus:border-[#FF4500] outline-none"
                  placeholder="e.g. Bunjil's Watch — timelapse"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-white/50">Sort order</label>
                <input
                  type="number"
                  value={videoForm.sort_order}
                  onChange={(e) => setVideoForm({ ...videoForm, sort_order: Number(e.target.value) })}
                  className="w-full bg-[#0A0A0A] border border-white/15 px-3 py-2 mt-1 text-sm focus:border-[#FF4500] outline-none"
                />
              </div>
              <div className="md:col-span-2">
                <label className="text-[10px] uppercase tracking-widest text-white/50">Caption (optional)</label>
                <textarea
                  value={videoForm.description}
                  onChange={(e) => setVideoForm({ ...videoForm, description: e.target.value })}
                  className="w-full bg-[#0A0A0A] border border-white/15 px-3 py-2 mt-1 text-sm focus:border-[#FF4500] outline-none min-h-[80px]"
                  placeholder="Short caption or story about this piece"
                />
              </div>
              <div className="md:col-span-2 flex items-center gap-3 pt-2">
                <button data-testid="admin-video-submit" type="submit" className="btn-primary text-sm py-2 px-4">
                  <Plus className="w-4 h-4" /> {videoEditingId ? "Save changes" : "Add video"}
                </button>
                {videoEditingId && (
                  <button type="button" onClick={resetVideoForm} className="text-xs uppercase tracking-widest text-white/60 hover:text-white">
                    Cancel edit
                  </button>
                )}
              </div>
            </form>

            {loading ? <p className="text-white/50">Loading...</p> : videos.length === 0 ? (
              <p className="text-white/50 text-center py-16">No videos yet. Drop your first process clip above.</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {videos.map((v) => (
                  <div key={v.id} data-testid={`admin-video-item-${v.id}`} className="border border-white/10 bg-[#141414] overflow-hidden">
                    <div className="aspect-video bg-black overflow-hidden">
                      <video src={v.video_url} controls preload="metadata" className="w-full h-full object-contain" />
                    </div>
                    <div className="p-3">
                      <p className="text-[10px] uppercase tracking-widest text-[#FF4500] font-semibold mb-1 flex items-center gap-1">
                        <Video className="w-3 h-3" /> Process video
                      </p>
                      <p className="font-display text-sm tracking-wider leading-tight mb-1 truncate">{v.title}</p>
                      <p className="text-[10px] text-white/40">Order: {v.sort_order}</p>
                      <div className="flex gap-2 mt-3">
                        <button onClick={() => editVideo(v)} className="flex-1 text-[10px] uppercase tracking-widest border border-white/20 py-1.5 hover:border-[#FF4500] hover:text-[#FF4500]">
                          <Pencil className="w-3 h-3 inline mr-1" /> Edit
                        </button>
                        <button onClick={() => deleteVideo(v.id)} className="text-[10px] uppercase tracking-widest border border-white/20 py-1.5 px-2 hover:border-red-500 hover:text-red-500">
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "gallery" && (
          <div data-testid="admin-panel-gallery">
            <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
              <div>
                <h2 className="font-display text-3xl">Studio Gallery</h2>
                <p className="text-xs text-white/50 uppercase tracking-widest mt-1">Post any artwork here — paintings, decorations, one-offs. Not tied to shop.</p>
              </div>
            </div>

            <form onSubmit={submitGallery} className="border border-white/10 bg-[#141414] p-5 md:p-6 grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
              <div className="md:col-span-2">
                <p className="overline mb-3">{galleryEditingId ? "Edit gallery item" : "Add a new piece to the gallery"}</p>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-white/50">Title *</label>
                <input
                  data-testid="admin-gallery-title"
                  value={galleryForm.title}
                  onChange={(e) => setGalleryForm({ ...galleryForm, title: e.target.value })}
                  className="w-full bg-[#0A0A0A] border border-white/15 px-3 py-2 mt-1 text-sm focus:border-[#FF4500] outline-none"
                  placeholder="e.g. Red Octopus Dreaming"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-white/50">Category</label>
                <select
                  data-testid="admin-gallery-category"
                  value={galleryForm.category}
                  onChange={(e) => setGalleryForm({ ...galleryForm, category: e.target.value })}
                  className="w-full bg-[#0A0A0A] border border-white/15 px-3 py-2 mt-1 text-sm focus:border-[#FF4500] outline-none"
                >
                  <option value="painting">Painting</option>
                  <option value="decoration">Decoration</option>
                  <option value="sketch">Sketch</option>
                  <option value="other">Other studio work</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="text-[10px] uppercase tracking-widest text-white/50">Image *</label>
                <div className="mt-2">
                  <ImageDropzone
                    value={galleryForm.image_url}
                    onUploaded={(url) => setGalleryForm({ ...galleryForm, image_url: url })}
                    onCleared={() => setGalleryForm({ ...galleryForm, image_url: "" })}
                    label="Drop image here or click to browse"
                  />
                </div>
              </div>
              <div className="md:col-span-2">
                <label className="text-[10px] uppercase tracking-widest text-white/50">Description (optional)</label>
                <textarea
                  data-testid="admin-gallery-description"
                  value={galleryForm.description}
                  onChange={(e) => setGalleryForm({ ...galleryForm, description: e.target.value })}
                  className="w-full bg-[#0A0A0A] border border-white/15 px-3 py-2 mt-1 text-sm focus:border-[#FF4500] outline-none min-h-[80px]"
                  placeholder="Short story or note about the piece"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-white/50">Sort order</label>
                <input
                  data-testid="admin-gallery-sort"
                  type="number"
                  value={galleryForm.sort_order}
                  onChange={(e) => setGalleryForm({ ...galleryForm, sort_order: Number(e.target.value) })}
                  className="w-full bg-[#0A0A0A] border border-white/15 px-3 py-2 mt-1 text-sm focus:border-[#FF4500] outline-none"
                />
              </div>
              <div className="md:col-span-2 flex items-center gap-3 pt-2">
                <button data-testid="admin-gallery-submit" type="submit" className="btn-primary text-sm py-2 px-4">
                  <Plus className="w-4 h-4" /> {galleryEditingId ? "Save changes" : "Add to gallery"}
                </button>
                {galleryEditingId && (
                  <button type="button" onClick={resetGalleryForm} className="text-xs uppercase tracking-widest text-white/60 hover:text-white">
                    Cancel edit
                  </button>
                )}
              </div>
            </form>

            {loading ? <p className="text-white/50">Loading...</p> : gallery.length === 0 ? (
              <p className="text-white/50 text-center py-16">No gallery items yet. Add your first piece above.</p>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {gallery.map((g) => (
                  <div key={g.id} data-testid={`admin-gallery-item-${g.id}`} className="border border-white/10 bg-[#141414] overflow-hidden">
                    <div className="aspect-square bg-black overflow-hidden">
                      <img src={g.image_url} alt={g.title} className="w-full h-full object-cover" />
                    </div>
                    <div className="p-3">
                      <p className="text-[10px] uppercase tracking-widest text-[#FF4500] font-semibold mb-1">{g.category}</p>
                      <p className="font-display text-sm tracking-wider leading-tight mb-1 truncate">{g.title}</p>
                      <p className="text-[10px] text-white/40">Order: {g.sort_order}</p>
                      <div className="flex gap-2 mt-3">
                        <button onClick={() => editGallery(g)} className="flex-1 text-[10px] uppercase tracking-widest border border-white/20 py-1.5 hover:border-[#FF4500] hover:text-[#FF4500]">
                          <Pencil className="w-3 h-3 inline mr-1" /> Edit
                        </button>
                        <button onClick={() => deleteGallery(g.id)} className="text-[10px] uppercase tracking-widest border border-white/20 py-1.5 px-2 hover:border-red-500 hover:text-red-500">
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "hoodie-logos" && (
          <div data-testid="admin-panel-hoodie-logos">
            <div className="mb-8 bg-[#141414] border border-white/10 p-6">
              <p className="overline mb-3">{logoEditingId ? "Edit hoodie logo" : "Add a new logo to the hoodie customiser"}</p>
              <form onSubmit={submitLogo} className="grid md:grid-cols-2 gap-4">
                <input
                  data-testid="admin-logo-label"
                  type="text"
                  placeholder="Label (e.g. Southern Cross)"
                  value={logoForm.label}
                  onChange={(e) => setLogoForm({ ...logoForm, label: e.target.value })}
                  className="md:col-span-1 bg-[#0A0A0A] border border-white/20 text-white px-3 py-2 focus:outline-none focus:border-[#FF4500]"
                />
                <input
                  data-testid="admin-logo-tag"
                  type="text"
                  placeholder="Tag / short subtitle (optional)"
                  value={logoForm.tag}
                  onChange={(e) => setLogoForm({ ...logoForm, tag: e.target.value })}
                  className="md:col-span-1 bg-[#0A0A0A] border border-white/20 text-white px-3 py-2 focus:outline-none focus:border-[#FF4500]"
                />
                <div className="md:col-span-2">
                  <label className="overline block mb-2">Logo image (PNG with transparent background recommended)</label>
                  <ImageDropzone
                    value={logoForm.image_url}
                    onUploaded={(url) => setLogoForm({ ...logoForm, image_url: url })}
                    onCleared={() => setLogoForm({ ...logoForm, image_url: "" })}
                  />
                </div>
                <label className="md:col-span-1 flex items-center gap-2 text-sm text-white/80">
                  <input
                    data-testid="admin-logo-recolourable"
                    type="checkbox"
                    checked={logoForm.recolourable}
                    onChange={(e) => setLogoForm({ ...logoForm, recolourable: e.target.checked })}
                    className="w-4 h-4 accent-[#FF4500]"
                  />
                  <span>
                    <span className="block">Single-colour design (font-colour tint applies)</span>
                    <span className="block text-[11px] text-white/50">Leave unchecked for multi-colour emblems that print as-is.</span>
                  </span>
                </label>
                <input
                  data-testid="admin-logo-sort-order"
                  type="number"
                  placeholder="Sort order"
                  value={logoForm.sort_order}
                  onChange={(e) => setLogoForm({ ...logoForm, sort_order: parseInt(e.target.value, 10) || 0 })}
                  className="md:col-span-1 bg-[#0A0A0A] border border-white/20 text-white px-3 py-2 focus:outline-none focus:border-[#FF4500]"
                />
                <div className="md:col-span-2 flex gap-3">
                  <button data-testid="admin-logo-submit" type="submit" className="px-6 py-2.5 bg-[#FF4500] text-black font-display tracking-wider hover:bg-[#FF4500]/80 transition-colors">
                    {logoEditingId ? "Save changes" : "Add logo"}
                  </button>
                  {logoEditingId && (
                    <button type="button" onClick={resetLogoForm} className="px-6 py-2.5 border border-white/20 text-white/80 font-display tracking-wider hover:border-white transition-colors">
                      Cancel
                    </button>
                  )}
                </div>
              </form>
            </div>

            {loading ? (
              <p className="text-white/50">Loading...</p>
            ) : hoodieLogos.length === 0 ? (
              <p className="text-white/50 text-center py-16">No logos yet — add the first one above.</p>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {hoodieLogos.map((l) => (
                  <div key={l.id} data-testid={`admin-logo-${l.id}`} className="bg-[#141414] border border-white/10 p-4 flex gap-4">
                    <div className="w-20 h-20 shrink-0 bg-white border border-white/10 flex items-center justify-center overflow-hidden">
                      {l.image_url ? (
                        <img src={l.image_url} alt={l.label} className="w-full h-full object-contain" />
                      ) : (
                        <span className="text-white/30 text-xs">No image</span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-display tracking-wider text-white text-sm truncate">{l.label}</p>
                        {l.recolourable && (
                          <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 border border-[#FF4500]/50 text-[#FF4500]">Recolourable</span>
                        )}
                      </div>
                      {l.tag && <p className="text-[11px] text-white/50 truncate">{l.tag}</p>}
                      <p className="text-[10px] text-white/30 mt-1">Sort {l.sort_order}</p>
                      <div className="flex gap-2 mt-3">
                        <button data-testid={`admin-logo-toggle-${l.id}`} onClick={() => toggleLogoVisible(l)} title={l.is_visible ? "Hide" : "Show"} className="p-1.5 border border-white/15 hover:border-white text-white/70 hover:text-white transition-colors">
                          {l.is_visible ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                        </button>
                        <button data-testid={`admin-logo-edit-${l.id}`} onClick={() => editLogo(l)} className="p-1.5 border border-white/15 hover:border-[#FF4500] text-white/70 hover:text-[#FF4500] transition-colors">
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button data-testid={`admin-logo-delete-${l.id}`} onClick={() => deleteLogo(l.id)} className="p-1.5 border border-white/15 hover:border-red-500 text-white/70 hover:text-red-400 transition-colors">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "reviews" && (
          <div data-testid="admin-panel-reviews">
            <div className="flex items-center gap-2 mb-6">
              {["pending", "approved", "all"].map((f) => (
                <button
                  key={f}
                  data-testid={`admin-reviews-filter-${f}`}
                  onClick={() => setReviewFilter(f)}
                  className={`px-4 py-1.5 text-[11px] uppercase tracking-widest border transition-colors ${
                    reviewFilter === f
                      ? "border-[#FF4500] text-[#FF4500] bg-[#FF4500]/5"
                      : "border-white/20 text-white/70 hover:border-white"
                  }`}
                >
                  {f} {f === "pending" && pendingCount > 0 && <span className="ml-1 text-white/60">({pendingCount})</span>}
                </button>
              ))}
            </div>

            {loading ? (
              <p className="text-white/50">Loading...</p>
            ) : filteredReviews.length === 0 ? (
              <p className="text-white/50 text-center py-16">
                {reviewFilter === "pending" ? "Nothing waiting for approval — nice work." : "No reviews yet."}
              </p>
            ) : (
              <div className="space-y-4">
                {filteredReviews.map((r) => {
                  const product = products.find((p) => p.id === r.product_id);
                  return (
                    <div
                      key={r.id}
                      data-testid={`admin-review-${r.id}`}
                      className={`border p-5 bg-[#141414] ${r.is_approved ? "border-white/10" : "border-[#FF4500]/40"}`}
                    >
                      <div className="flex items-start justify-between gap-4 mb-3">
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-display tracking-wider text-white">{r.author_name}</p>
                            <span className={`text-[9px] uppercase tracking-widest px-1.5 py-0.5 border ${r.is_approved ? "border-white/20 text-white/60" : "border-[#FF4500]/50 text-[#FF4500]"}`}>
                              {r.is_approved ? "Approved" : "Pending"}
                            </span>
                            <span className="text-yellow-400 text-xs">{"★".repeat(r.rating)}<span className="text-white/25">{"★".repeat(5 - r.rating)}</span></span>
                          </div>
                          <p className="text-[11px] text-white/40 mt-1">
                            for <span className="text-white/70">{product?.title || r.product_id}</span> · {new Date(r.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="flex gap-2 shrink-0">
                          <button
                            data-testid={`admin-review-approve-${r.id}`}
                            onClick={() => approveReview(r)}
                            className={`px-3 py-1.5 text-[10px] uppercase tracking-widest border transition-colors ${
                              r.is_approved
                                ? "border-white/20 text-white/70 hover:border-white"
                                : "border-[#FF4500] text-[#FF4500] hover:bg-[#FF4500] hover:text-black"
                            }`}
                          >
                            {r.is_approved ? "Unapprove" : "Approve"}
                          </button>
                          <button
                            data-testid={`admin-review-delete-${r.id}`}
                            onClick={() => deleteReview(r.id)}
                            className="p-1.5 border border-white/15 hover:border-red-500 text-white/70 hover:text-red-400 transition-colors"
                            aria-label="Delete"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                      <p className="text-white/80 leading-relaxed text-sm whitespace-pre-line">{r.body}</p>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
      <ProductFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        product={editing}
        onSaved={loadAll}
      />
    </div>
  );
}
